import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { languages, getPathWithoutLanguage, getLocalizedPath } from '../i18n';
import { SITE_NAME, SITE_URL } from '../config/site';

const OG_LOCALES = {
  uk: 'uk_UA',
  en: 'en_US',
  de: 'de_DE',
  lt: 'lt_LT'
};

function normalizeToArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function absolutize(urlOrPath) {
  if (!urlOrPath) return null;
  if (/^https?:\/\//i.test(urlOrPath)) return urlOrPath;
  const path = urlOrPath.startsWith('/') ? urlOrPath : `/${urlOrPath}`;
  return `${SITE_URL}${path}`;
}

/**
 * SEO helper for SPA pages:
 * - title + meta description per locale
 * - canonical + hreflang alternates
 * - OpenGraph + Twitter
 * - optional JSON-LD
 */
export default function SEO({
  title,
  description,
  image = '/logo512.png',
  type = 'website',
  noindex = false,
  canonicalPath,
  jsonLd
}) {
  const location = useLocation();
  const params = useParams();
  const { i18n, t } = useTranslation();

  const language = useMemo(() => {
    const candidate = params.language || i18n.language || 'en';
    return languages.includes(candidate) ? candidate : 'en';
  }, [params.language, i18n.language]);

  const pathWithoutLanguage = useMemo(
    () => getPathWithoutLanguage(location.pathname || '/'),
    [location.pathname]
  );

  const localizedPath = useMemo(
    () => getLocalizedPath(pathWithoutLanguage, language),
    [pathWithoutLanguage, language]
  );

  const canonicalUrl = useMemo(() => {
    if (canonicalPath) return `${SITE_URL}${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`;
    return `${SITE_URL}${localizedPath}`;
  }, [canonicalPath, localizedPath]);

  const alternateLinks = useMemo(() => {
    return languages.map((lng) => ({
      lng,
      href: `${SITE_URL}${getLocalizedPath(pathWithoutLanguage, lng)}`
    }));
  }, [pathWithoutLanguage]);

  const resolvedTitle =
    title ||
    t('seo.defaults.title', {
      defaultValue: `${SITE_NAME} — private mental wellbeing companion 24/7`
    });

  const resolvedDescription =
    description ||
    t('seo.defaults.description', {
      defaultValue:
        'AI PSY HELP is confidential 24/7 wellbeing support: voice or text chat, short check-ins and personal insights. Not diagnosis, not emergency care.'
    });

  const ogImage = absolutize(image);
  const jsonLdItems = normalizeToArray(jsonLd);

  return (
    <Helmet prioritizeSeoTags>
      <html lang={language} />
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow'} />

      <link rel="canonical" href={canonicalUrl} />
      {alternateLinks.map(({ lng, href }) => (
        <link key={lng} rel="alternate" hrefLang={lng} href={href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${getLocalizedPath(pathWithoutLanguage, 'en')}`} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      <meta property="og:locale" content={OG_LOCALES[language] || 'en_US'} />
      {languages
        .filter((lng) => lng !== language)
        .map((lng) => (
          <meta key={lng} property="og:locale:alternate" content={OG_LOCALES[lng] || 'en_US'} />
        ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}

      {jsonLdItems.map((item, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}

